import React from 'react'
import clsx from 'clsx'
import { PageTitle } from '_metronic/layout/core'
import { WithChildren, KTSVG } from '_metronic/helpers'
import PageBreadCrumb from 'Presentation/Components/PageBreadCrumb'
import CustomTablePagination from 'Presentation/Components/Table/Components/CustomTablePagination'
import TableLoading from 'Presentation/Components/Table/Components/TableLoading'
import DateRangePicker from 'Presentation/Components/DateRangePicker'

import useViewModel from './ViewModel'

const COLUMN_COUNT = 7

const HEADER_STYLE: React.CSSProperties = {
  color: '#475467',
  textTransform: 'none',
  paddingLeft: '1.25rem',
  paddingRight: '1.25rem',
}

const STATUS_LABEL: Record<string, string> = {
  success: 'Success',
  failed: 'Failed',
}

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

const Highlight: React.FC<{ text: string; term: string }> = ({ text, term }) => {
  const trimmed = term.trim()
  if (!trimmed) return <>{text}</>

  const parts = text.split(new RegExp(`(${escapeRegExp(trimmed)})`, 'gi'))
  const lowerTerm = trimmed.toLowerCase()

  return (
    <>
      {parts.map((part, idx) =>
        part.toLowerCase() === lowerTerm ? (
          <mark key={idx} className='p-0 bg-warning'>
            {part}
          </mark>
        ) : (
          <React.Fragment key={idx}>{part}</React.Fragment>
        )
      )}
    </>
  )
}

const DetailItem: React.FC<{ label: string; value?: string | number | null }> = ({
  label,
  value,
}) => (
  <div className='col-12 col-md-6 col-lg-4 mb-4'>
    <div className='fw-bold text-muted mb-1'>{label}</div>
    <div className='text-gray-800 text-break'>{value === null || value === undefined || value === '' ? '-' : value}</div>
  </div>
)

const ReceiveScanLogListView: React.FC = () => {
  const {
    data,
    totalCount,
    isLoading,
    isError,
    page,
    pageSize,
    search,
    onSearchChange,
    statusFilter,
    statusOptions,
    onStatusFilterChange,
    projectId,
    onProjectIdChange,
    dateFrom,
    dateTo,
    onDateRangeChange,
    clearFilters,
    isExpanded,
    sortField,
    sortDirection,
    onSort,
    toggleExpand,
    goToPage,
    formatDate,
  } = useViewModel()

  const renderSortableHeader = (field: any, label: string) => {
    const isSorted = sortField === field
    return (
      <th
        className='pt-2 pb-3 position-relative cursor-pointer table-header-sortable'
        style={HEADER_STYLE}
        onClick={() => onSort(field)}>
        <span className='table-header-sortable-label'>{label}</span>
        {isSorted && (
          <span className='position-absolute right-0'>
            <KTSVG
              path={
                sortDirection === 'desc'
                  ? '/media/icons/duotune/arrows/arr004.svg'
                  : '/media/icons/duotune/arrows/arr003.svg'
              }
              className='svg-icon-2 svg-icon-primary'
            />
          </span>
        )}
      </th>
    )
  }

  return (
    <ViewPageTitle>
      <div className='row gy-5 mb-5'>
        <div className='col-12'>
          <PageBreadCrumb />
        </div>

        <div className='col-12'>
          <div className='card'>
            <div className='card-header'>
              <div className='card-title'>
                <h3 className='fw-bold m-0'>Receive Scan Log</h3>
              </div>
              <div className='card-toolbar'>
                <div className='d-flex align-items-center flex-wrap gap-3'>
                  <div className='d-flex align-items-center'>
                    <span className='text-muted fs-7 fw-bold me-2'>วันที่</span>
                    <DateRangePicker
                      className='w-250px'
                      from={dateFrom}
                      to={dateTo}
                      maxDate={new Date()}
                      onChange={onDateRangeChange}
                    />
                  </div>

                  <input
                    type='text'
                    inputMode='numeric'
                    className='form-control form-control-solid w-125px'
                    placeholder='Project ID'
                    value={projectId}
                    onChange={e => onProjectIdChange(e.target.value)}
                  />

                  <select
                    className='form-select form-select-solid w-150px'
                    value={statusFilter}
                    onChange={e => onStatusFilterChange(e.target.value)}>
                    <option value=''>สถานะทั้งหมด</option>
                    {statusOptions.map(status => (
                      <option key={status} value={status}>
                        {STATUS_LABEL[status] ?? status}
                      </option>
                    ))}
                  </select>

                  <div className='d-flex align-items-center position-relative'>
                    <KTSVG
                      path='/media/icons/duotune/general/gen021.svg'
                      className='svg-icon-1 position-absolute ms-4'
                    />
                    <input
                      type='text'
                      className='form-control form-control-solid w-250px ps-12 pe-10'
                      placeholder='ค้นหา Inventory Code'
                      value={search}
                      onChange={e => onSearchChange(e.target.value)}
                    />
                    {search && (
                      <button
                        type='button'
                        className='btn btn-icon btn-sm position-absolute end-0 me-2'
                        onClick={() => onSearchChange('')}
                        aria-label='Clear search'>
                        <KTSVG
                          path='/media/icons/duotune/arrows/arr061.svg'
                          className='svg-icon-2'
                        />
                      </button>
                    )}
                  </div>

                  <button
                    type='button'
                    className='btn btn-light-danger btn-sm'
                    onClick={clearFilters}>
                    ล้างตัวกรอง
                  </button>
                </div>
              </div>
            </div>

            <div className='card-body'>
              <div className='position-relative'>
                <div className='table-responsive'>
                  <table className='table align-middle table-row-dashed fs-6 gy-5 dataTable no-footer'>
                    <thead>
                      <tr className='text-start text-muted fw-bold fs-7 text-uppercase gs-0'>
                        <th style={{ width: 40 }}></th>
                        {renderSortableHeader('createdat', 'Created At')}
                        {renderSortableHeader('projectid', 'Project ID')}
                        {renderSortableHeader('inventorycode', 'Inventory Code')}
                        {renderSortableHeader('status', 'Status')}
                        <th className='pt-2 pb-3' style={HEADER_STYLE}>
                          Reason
                        </th>
                        <th className='pt-2 pb-3' style={HEADER_STYLE}>
                          Scanned By
                        </th>
                      </tr>
                    </thead>
                    <tbody className='text-gray-800'>
                      {data.length > 0 ? (
                        data.map((log, i) => (
                          <React.Fragment key={log.id}>
                            <tr className={clsx({ 'bg-gray-100': i % 2 === 0 })}>
                              <td>
                                <button
                                  type='button'
                                  className='btn btn-icon btn-sm btn-light-primary'
                                  onClick={() => toggleExpand(log.id)}
                                  aria-label='Toggle detail'>
                                  <KTSVG
                                    path={
                                      isExpanded(log.id)
                                        ? '/media/icons/duotune/arrows/arr073.svg'
                                        : '/media/icons/duotune/arrows/arr072.svg'
                                    }
                                    className='svg-icon-2'
                                  />
                                </button>
                              </td>
                              <td className='text-nowrap'>{formatDate(log.createdAt)}</td>
                              <td>{log.projectId ?? '-'}</td>
                              <td>
                                <span className='fw-bold text-gray-800'>
                                  <Highlight text={log.inventoryCode} term={search} />
                                </span>
                              </td>
                              <td>
                                <span
                                  className={`badge fw-bold text-uppercase ${
                                    log.status === 'success'
                                      ? 'badge-light-success'
                                      : 'badge-light-danger'
                                  }`}>
                                  {STATUS_LABEL[log.status] ?? log.status}
                                </span>
                              </td>
                              <td>{log.errorReason ?? '-'}</td>
                              <td>
                                {log.scannedByFullName || log.scannedByUsername ? (
                                  <div className='d-flex flex-column'>
                                    <span className='fw-semibold'>
                                      {log.scannedByFullName ?? '-'}
                                    </span>
                                    {log.scannedByUsername && (
                                      <span className='text-muted fs-7'>
                                        @{log.scannedByUsername}
                                      </span>
                                    )}
                                  </div>
                                ) : (
                                  '-'
                                )}
                              </td>
                            </tr>
                            {isExpanded(log.id) && (
                              <tr className={clsx({ 'bg-gray-100': i % 2 === 0 })}>
                                <td colSpan={COLUMN_COUNT}>
                                  <div className='p-4'>
                                    <div className='row'>
                                      <DetailItem label='Log ID' value={log.id} />
                                      <DetailItem label='Inventory ID' value={log.inventoryId} />
                                      <DetailItem
                                        label='Inventory Code (D365)'
                                        value={log.inventoryCodeD365}
                                      />
                                      <DetailItem label='Serial Type' value={log.serialType} />
                                      {/* <DetailItem
                                        label='Serial Type Source'
                                        value={log.serialTypeSource}
                                      /> */}
                                      <DetailItem
                                        label='Scanned By User ID'
                                        value={log.scannedByUserId}
                                      />
                                      {/* <DetailItem label='IP Address' value={log.ipAddress} /> */}
                                      {/* <DetailItem label='User Agent' value={log.userAgent} /> */}
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={COLUMN_COUNT}>
                            <div className='d-flex text-center w-100 align-content-center justify-content-center py-6'>
                              {isLoading
                                ? 'Loading...'
                                : isError
                                ? 'เกิดข้อผิดพลาดในการดึงข้อมูล'
                                : 'No matching records found'}
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                {isLoading && <TableLoading />}
              </div>

              {totalCount > pageSize && (
                <CustomTablePagination
                  isLoading={isLoading}
                  totalCountData={totalCount}
                  page={page}
                  items_per_page={pageSize}
                  updatePage={goToPage}
                  links={[]}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </ViewPageTitle>
  )
}

const ViewPageTitle: React.FC<WithChildren> = ({ children }) => {
  return (
    <>
      <PageTitle
        breadcrumbs={[
          {
            isActive: true,
            isSeparator: false,
            path: '#',
            title: 'Receive Scan Log',
            icon: '/media/icons/duotune/general/gen005.svg',
          },
        ]}>
        Receive Scan Log
      </PageTitle>

      {children}
    </>
  )
}

export default ReceiveScanLogListView
